import { createFileRoute } from "@tanstack/react-router";
import { PageLayout } from "../../components/PageLayout";

export const Route = createFileRoute("/_layout/privacy")({
    head: () => ({
        meta: [
            { name: "title", content: "Privacy Policy - StoryDoodle" },
            {
                name: "description",
                content:
                    "Privacy Policy for StoryDoodle - How we protect your family's data in our AI-powered storytelling platform.",
            },
            { property: "og:title", content: "Privacy Policy - StoryDoodle" },
            {
                property: "og:description",
                content:
                    "Privacy Policy for StoryDoodle - How we protect your family's data in our AI-powered storytelling platform.",
            },
            { property: "og:url", content: "https://storydoodle.app/privacy" },
        ],
    }),
    component: Privacy,
});

function Privacy() {
    return (
        <PageLayout title="Privacy Policy" breadcrumbs={[{ label: "Home", to: "/" }, { label: "Privacy Info" }]}>
            <main className="container mx-auto px-4 sm:px-6 py-8 max-w-4xl">
                <div className="space-y-8">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
                        <p className="text-muted-foreground">Last updated: July 8, 2025</p>
                    </div>

                    <div className="prose prose-lg max-w-none space-y-6">
                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Geographic Restrictions</h2>
                            <p className="text-foreground leading-relaxed">
                                StoryDoodle is only available to residents of the United States. This service is not
                                available to users located in the European Union or other restricted jurisdictions. By
                                accessing this service, you confirm that you are a US resident.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Age Requirements</h2>
                            <p className="text-foreground leading-relaxed">
                                StoryDoodle requires users to be 13 years of age or older to create an account. Children
                                under 13 are welcome to use StoryDoodle with supervision from a parent or guardian who
                                creates the account on their behalf.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Information We Collect</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    We collect the following categories of personal information:
                                </p>

                                <div className="space-y-3">
                                    <p className="text-foreground leading-relaxed">
                                        <strong>Profile/Contact Data:</strong>
                                    </p>
                                    <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                        <li>Email address for account creation and authentication</li>
                                        <li>Account preferences and settings</li>
                                    </ul>

                                    <p className="text-foreground leading-relaxed">
                                        <strong>Commercial Data:</strong>
                                    </p>
                                    <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                        <li>Drawings and stories you create within the app</li>
                                        <li>Usage patterns and feature interactions</li>
                                        <li>Subscription history and billing records</li>
                                    </ul>

                                    <p className="text-foreground leading-relaxed">
                                        <strong>Device/IP Data:</strong>
                                    </p>
                                    <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                        <li>Device and browser information for technical support</li>
                                        <li>IP address and general location data</li>
                                        <li>Session information and access logs</li>
                                    </ul>

                                    <p className="text-foreground leading-relaxed">
                                        <strong>Payment Data (for paid subscriptions):</strong>
                                    </p>
                                    <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                        <li>Payment information processed by Polar.sh and Stripe</li>
                                        <li>Billing address and contact information</li>
                                        <li>Customer reference ID for subscription management</li>
                                        <li>Tax identification information when required</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">How We Use Your Information</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">We use your information to:</p>
                                <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                    <li>Provide and improve the StoryDoodle service</li>
                                    <li>Save your stories and drawings so you can access them later</li>
                                    <li>Generate AI-powered story content based on your drawings</li>
                                    <li>Send important updates about your account or our service</li>
                                    <li>Process subscription payments and manage billing</li>
                                    <li>Provide customer support for billing and account issues</li>
                                    <li>Comply with tax and legal requirements for payment processing</li>
                                    <li>Prevent fraud and unauthorized access to paid features</li>
                                </ul>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Data Security & Privacy</h2>
                            <p className="text-foreground leading-relaxed">
                                We take data security seriously. Your information is stored securely and we do not share
                                your personal information with third parties except as necessary to provide our service
                                (such as using secure cloud storage and AI processing services).
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Data Retention & Your Rights</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    <strong>Your Data Rights:</strong>
                                </p>
                                <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                    <li>
                                        <strong>Access:</strong> Request a copy of your personal data
                                    </li>
                                    <li>
                                        <strong>Correction:</strong> Update or correct inaccurate information
                                    </li>
                                    <li>
                                        <strong>Deletion:</strong> Delete individual stories, drawings, or your entire
                                        account
                                    </li>
                                    <li>
                                        <strong>Portability:</strong> Export your content in standard formats
                                    </li>
                                    <li>
                                        <strong>Opt-out:</strong> Unsubscribe from marketing communications
                                    </li>
                                </ul>

                                <p className="text-foreground leading-relaxed">
                                    <strong>Data Retention:</strong>
                                </p>
                                <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                    <li>Content data is retained as long as you have an open account</li>
                                    <li>
                                        Account data may be retained longer for legal obligations or dispute resolution
                                    </li>
                                    <li>Parents can exercise these rights for accounts used by their children</li>
                                </ul>

                                <p className="text-foreground leading-relaxed">
                                    <strong>Payment and billing data:</strong> Subscription and payment history is
                                    retained as required for tax compliance, dispute resolution, and regulatory
                                    requirements. This information may be retained even after account deletion as
                                    permitted by law.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Third-Party Services</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    StoryDoodle uses trusted third-party services to provide our features:
                                </p>
                                <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                    <li>Secure cloud database and authentication services</li>
                                    <li>AI content generation and image analysis services</li>
                                    <li>Analytics and error monitoring services</li>
                                    <li>
                                        <strong>Polar.sh:</strong> Payment processing and subscription management
                                    </li>
                                    <li>
                                        <strong>Stripe:</strong> Underlying payment processing (via Polar.sh)
                                    </li>
                                </ul>
                                <p className="text-foreground leading-relaxed">
                                    These services have their own privacy policies and security measures. Payment
                                    information is handled securely by Polar.sh and Stripe according to industry
                                    standards and regulatory requirements.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">
                                Payment Data Handling & Merchant of Record
                            </h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    <strong>Merchant of Record:</strong> Polar.sh acts as the merchant of record for all
                                    transactions. When you subscribe to a paid plan:
                                </p>
                                <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                    <li>
                                        Payment card information is processed securely by Stripe and not stored by
                                        StoryDoodle
                                    </li>
                                    <li>
                                        We store only a customer reference ID provided by Polar.sh for subscription
                                        management
                                    </li>
                                    <li>Billing information is retained for tax compliance and dispute resolution</li>
                                    <li>
                                        You can access and manage your payment information through the billing portal
                                    </li>
                                    <li>Tax identification information may be collected for compliance purposes</li>
                                </ul>
                                <p className="text-foreground leading-relaxed">
                                    <strong>Data Sharing:</strong> Payment data is processed by Polar.sh (merchant of
                                    record) and Stripe (payment processor). This data is subject to their respective
                                    privacy policies. We do not have access to your full payment card details.
                                </p>
                                <p className="text-foreground leading-relaxed">
                                    <strong>Data Transfer:</strong> Payment processing may involve international data
                                    transfers to countries where Polar.sh and Stripe operate, which may have different
                                    data protection laws.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Parental Rights</h2>
                            <p className="text-foreground leading-relaxed">
                                Parents who create accounts for use with their children have the right to:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                <li>Review all content created on the account</li>
                                <li>Delete individual stories or drawings</li>
                                <li>Delete the entire account and all associated data</li>
                                <li>Contact us with any concerns about the account or content</li>
                            </ul>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Contact Us</h2>
                            <p className="text-foreground leading-relaxed">
                                If you have questions about privacy or need help with your account, please{" "}
                                <a href="/help" className="text-primary hover:underline font-medium">
                                    visit our help page
                                </a>{" "}
                                or email us at{" "}
                                <a
                                    href="mailto:help@storydoodle.app"
                                    className="text-primary hover:underline font-medium"
                                >
                                    help@storydoodle.app
                                </a>
                                .
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Cookies & Tracking</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    We use cookies and similar technologies to:
                                </p>
                                <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                    <li>Maintain your login session</li>
                                    <li>Remember your preferences and settings</li>
                                    <li>Analyze usage patterns for service improvement</li>
                                    <li>Improve service performance and user experience</li>
                                </ul>
                                <p className="text-foreground leading-relaxed">
                                    You can manage cookie preferences through your browser settings. Disabling certain
                                    cookies may affect service functionality.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Changes to This Policy</h2>
                            <p className="text-foreground leading-relaxed">
                                We may update this privacy policy from time to time. We will notify users of any
                                significant changes by email or through the app. Continued use of the service after
                                changes constitutes acceptance of the updated policy.
                            </p>
                        </section>
                    </div>
                </div>
            </main>
        </PageLayout>
    );
}
